import { clsx } from "clsx";
import { XIcon } from "lucide-react";
import { useIsomorphicLayoutEffect } from "../../hooks/useIsomorphicLayoutEffect";
import { useTranslations } from "../../i18n";
import { IconButton } from "../Button/IconButton";
import { Heading } from "../Heading";
import { Text } from "../Text";
import { useDialogContext } from "./context";
import type { IDialogHeaderProps } from "./types";

export const DialogHeader = ({
  title,
  description,
  showClose = true,
  className,
  children,
}: IDialogHeaderProps) => {
  const {
    titleId,
    descriptionId,
    preventClose,
    close,
    setHasTitle,
    setHasDescription,
  } = useDialogContext();
  const { t } = useTranslations();

  const hasTitle = Boolean(title);
  const hasDescription = Boolean(description);

  // Layout effect: the panel gets its name before the first paint.
  useIsomorphicLayoutEffect(() => {
    if (!hasTitle) return;
    setHasTitle(true);
    return () => setHasTitle(false);
  }, [hasTitle, setHasTitle]);

  useIsomorphicLayoutEffect(() => {
    if (!hasDescription) return;
    setHasDescription(true);
    return () => setHasDescription(false);
  }, [hasDescription, setHasDescription]);

  return (
    <header className={clsx("nuco-dialog__header", className)}>
      {(hasTitle || hasDescription) && (
        <div className="nuco-dialog__heading">
          {hasTitle && (
            <Heading level={3} id={titleId} as="h2">
              {title}
            </Heading>
          )}
          {hasDescription && (
            <Text
              size="lg"
              id={descriptionId}
              as="div"
              className="nuco-dialog__description"
            >
              {description}
            </Text>
          )}
        </div>
      )}
      {children}
      {showClose && !preventClose && (
        <IconButton
          variant="ghost"
          color="neutral"
          size="md"
          className="nuco-dialog__close"
          onClick={close}
          aria-label={t("ui.dialog.closeLabel")}
        >
          <XIcon />
        </IconButton>
      )}
    </header>
  );
};
